import { i18n, type MessageDescriptor } from '@lingui/core'
import { msg, t } from '@lingui/core/macro'
import type { SubjectKind, SubjectValidationStatus } from 'features/fim'
import { slotCounts } from 'features/fim/assetInventory'
import {
  facilityModelSortSchema,
  type FacilityModelView,
} from 'routes/facility-model/-searchParams'

import { type FacilityModelRecord, subjectSubsystem } from './catalog'
import { fillStatusLabel } from './views'

export type FacilityModelColumnKey =
  | 'name'
  | 'kind'
  | 'type'
  | 'subsystem'
  | 'lifecycle'
  | 'fillStatus'
  | 'slots'
  | 'template'
  | 'sources'
  | 'confidence'
  | 'validation'

export interface FacilityModelColumn {
  key: FacilityModelColumnKey
  label: MessageDescriptor
  /** Grid track for this column. */
  width: string
}

export const SORT_SCHEMA_KEYS: readonly FacilityModelColumnKey[] =
  facilityModelSortSchema.options

export const EMPTY_CELL = '—'

export const VALIDATION_LABELS: Record<
  SubjectValidationStatus,
  MessageDescriptor
> = {
  validated: msg`Validated`,
  stale: msg`Needs revalidation`,
  unvalidated: msg`Not validated`,
}

export const validationLabel = (status: string): string => {
  const label = (VALIDATION_LABELS as Record<string, MessageDescriptor>)[status]
  return label ? i18n._(label) : status
}

export const KIND_LABELS: Record<SubjectKind, MessageDescriptor> = {
  equipment: msg`Equipment`,
  instrument: msg`Instrument`,
  system: msg`System`,
  subsystem: msg`Subsystem`,
  pipeline: msg`Pipeline`,
  doc_reference: msg`Doc reference`,
  rejected: msg`Rejected`,
  non_asset: msg`Non-asset`,
}

export const kindLabel = (kind: string): string => {
  const label = (KIND_LABELS as Record<string, MessageDescriptor>)[kind]
  return label ? i18n._(label) : kind
}

const COLUMNS: Record<FacilityModelColumnKey, FacilityModelColumn> = {
  name: { key: 'name', label: msg`Name`, width: 'minmax(12rem, 2.4fr)' },
  kind: { key: 'kind', label: msg`Kind`, width: 'minmax(6rem, 0.8fr)' },
  type: { key: 'type', label: msg`Type`, width: 'minmax(7rem, 1.2fr)' },
  subsystem: {
    key: 'subsystem',
    label: msg`System`,
    width: 'minmax(7rem, 1fr)',
  },
  lifecycle: {
    key: 'lifecycle',
    label: msg`Model`,
    width: 'minmax(7rem, 0.9fr)',
  },
  fillStatus: {
    key: 'fillStatus',
    label: msg`Fill status`,
    width: 'minmax(7rem, 1fr)',
  },
  slots: { key: 'slots', label: msg`Slots`, width: 'minmax(4.5rem, 0.6fr)' },
  template: {
    key: 'template',
    label: msg`Template`,
    width: 'minmax(7rem, 1.1fr)',
  },
  sources: {
    key: 'sources',
    label: msg`Sources`,
    width: 'minmax(4.5rem, 0.5fr)',
  },
  confidence: {
    key: 'confidence',
    label: msg`Confidence`,
    width: 'minmax(5.5rem, 0.6fr)',
  },
  validation: {
    key: 'validation',
    label: msg`Validation`,
    width: 'minmax(8rem, 1fr)',
  },
}

/**
 * Columns adapt to the active preset: Assets leads with fill health, slot
 * coverage and template; the Subject presets carry the evidence weight behind
 * the row (sources and existence confidence) instead, since neither exists on
 * an Asset Instance.
 */
const VIEW_COLUMN_KEYS: Record<FacilityModelView, FacilityModelColumnKey[]> = {
  assets: [
    'name',
    'kind',
    'type',
    'subsystem',
    'fillStatus',
    'slots',
    'template',
    'lifecycle',
    'validation',
  ],
  equipment: [
    'name',
    'type',
    'subsystem',
    'sources',
    'confidence',
    'validation',
    'lifecycle',
    'fillStatus',
  ],
  instruments: [
    'name',
    'type',
    'subsystem',
    'sources',
    'confidence',
    'validation',
    'lifecycle',
    'fillStatus',
  ],
  all: [
    'name',
    'kind',
    'type',
    'subsystem',
    'sources',
    'confidence',
    'lifecycle',
    'fillStatus',
    'template',
    'validation',
  ],
  'needs-review': [
    'name',
    'kind',
    'type',
    'subsystem',
    'sources',
    'confidence',
    'validation',
    'lifecycle',
    'fillStatus',
  ],
}

export const columnsForView = (
  view: FacilityModelView
): FacilityModelColumn[] => VIEW_COLUMN_KEYS[view].map((key) => COLUMNS[key])

export const recordValidationStatus = (
  record: FacilityModelRecord
): SubjectValidationStatus =>
  record.subject.data.validation_status ?? 'unvalidated'

export const sourceCount = (record: FacilityModelRecord): number =>
  record.subject.data.source_set?.length ?? 0

export const confidencePercent = (record: FacilityModelRecord): number =>
  Math.min(
    100,
    Math.max(0, Math.round(record.subject.data.existence_confidence * 100))
  )

/** "<filled> filled, <gaps> gaps of <total>", or undefined without a fill. */
export const slotCoverageTitle = (
  record: FacilityModelRecord
): string | undefined => {
  const fill = record.assetInstance?.data.fill
  if (!fill) return undefined
  const { filledCount, gapCount, totalCount } = slotCounts(fill)
  if (totalCount === 0) return undefined
  return t`${filledCount} filled, ${gapCount} gaps of ${totalCount}`
}

/** One collator for the whole catalog: constructing one per comparison is
 * the dominant cost of sorting 14k rows. Natural ordering so -0121 < -0125. */
export const FACILITY_MODEL_COLLATOR = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
})

export type FacilityModelSortDirection = 'asc' | 'desc'

/** Metric columns read best largest-first; text columns A→Z. */
const DESC_FIRST: ReadonlySet<FacilityModelColumnKey> = new Set([
  'sources',
  'confidence',
  'slots',
])

export const defaultSortDirection = (
  key: FacilityModelColumnKey
): FacilityModelSortDirection => (DESC_FIRST.has(key) ? 'desc' : 'asc')

/** null sorts last in either direction (no fill, no value). Slots order by
 * completeness, so a 3/24 fill ranks under a 20/24 one. Text columns sort on
 * the stored value, never the translated label. */
const sortValue = (
  record: FacilityModelRecord,
  key: FacilityModelColumnKey
): number | string | null => {
  const { subject, assetInstance } = record
  switch (key) {
    case 'sources':
      return sourceCount(record)
    case 'confidence':
      return confidencePercent(record)
    case 'slots': {
      const { filledCount, totalCount } = slotCounts(assetInstance?.data.fill)
      return totalCount === 0 ? null : filledCount / totalCount
    }
    case 'name':
      return subject.data.name
    case 'kind':
      return subject.data.kind || null
    case 'type':
      return subject.data.type || null
    case 'subsystem':
      return subjectSubsystem(subject) || null
    case 'lifecycle':
      return assetInstance ? 'promoted' : 'discovered'
    case 'fillStatus':
      return assetInstance?.data.fill_status ?? null
    case 'template':
      return assetInstance?.data.template_id || null
    case 'validation':
      return recordValidationStatus(record)
  }
}

export const sortFacilityModelRecords = (
  records: readonly FacilityModelRecord[],
  key: FacilityModelColumnKey,
  direction: FacilityModelSortDirection
): FacilityModelRecord[] => {
  const factor = direction === 'desc' ? -1 : 1
  return [...records].sort((left, right) => {
    const leftValue = sortValue(left, key)
    const rightValue = sortValue(right, key)
    if (leftValue === null && rightValue === null) return 0
    if (leftValue === null) return 1
    if (rightValue === null) return -1
    const compared =
      typeof leftValue === 'number' && typeof rightValue === 'number'
        ? leftValue - rightValue
        : FACILITY_MODEL_COLLATOR.compare(String(leftValue), String(rightValue))
    if (compared !== 0) return factor * compared
    return FACILITY_MODEL_COLLATOR.compare(
      left.subject.data.name,
      right.subject.data.name
    )
  })
}

/** Text for one catalog cell. Fill status and template are Asset Instance
 * overlays, so they read as empty on an unpromoted Subject. */
export const facilityModelCellText = (
  record: FacilityModelRecord,
  key: FacilityModelColumnKey
): string => {
  const { subject, assetInstance } = record
  switch (key) {
    case 'name':
      return subject.data.name
    case 'kind':
      return kindLabel(subject.data.kind) || EMPTY_CELL
    case 'type':
      return subject.data.type || EMPTY_CELL
    case 'subsystem':
      return subjectSubsystem(subject) || EMPTY_CELL
    case 'lifecycle':
      return assetInstance ? t`Promoted` : t`Discovered`
    case 'fillStatus':
      return assetInstance
        ? fillStatusLabel(assetInstance.data.fill_status)
        : EMPTY_CELL
    case 'slots': {
      const { filledCount, totalCount } = slotCounts(assetInstance?.data.fill)
      return totalCount === 0 ? EMPTY_CELL : `${filledCount}/${totalCount}`
    }
    case 'template':
      return assetInstance?.data.template_id || EMPTY_CELL
    case 'sources':
      return `${sourceCount(record)}`
    case 'confidence':
      return `${confidencePercent(record)}%`
    case 'validation':
      return validationLabel(recordValidationStatus(record))
  }
}
